import React, { useState, useEffect, useRef, useMemo } from "react";
import useSWR from "swr";
import { useNavigate, Link } from "react-router-dom";
import {
  DataGrid,
  Column,
  Editing,
  Texts,
  Lookup,
  Button,
  Popup,
  Form,
  Item,
  SearchPanel,
  Scrolling,
  RequiredRule,
  PatternRule,
  EmailRule,
  Label,
  Toolbar,
  HeaderFilter,
  Search,
} from "devextreme-react/data-grid";
import { Button as MyButton } from "devextreme-react/button";
import { Popup as MyPopup, ToolbarItem } from "devextreme-react/popup";
import { FileUploader } from "devextreme-react/file-uploader";
import LoadingSpinnerModal from "../../Components/LoadingSpinnerModal";
import ErrorMessage from "../../Components/ErrorMessage";
import ActionNotifications from "../../Components/ActionNotifications";
import useFetch from "../../Hooks/useFetch";
import { apiBaseURL } from "../../Utils/ProjectConstants";
import { checkRoles } from "../../Utils/helper";
import { useScreenSize } from "../../Utils/media-query";

const rolesOptions = [
  //fulltime check
  { label: "Administrator", value: "88521846-a028-4c33-b157-d6f90fe0e6ef" },
  { label: "Program-Manager", value: "66242088-31fa-41cc-8d2b-c5f97fd44ce6" },
  { label: "Lab-Manager", value: "685632fc-af3a-464a-8c98-e11530d68d80" },
  { label: "Research-Engineer", value: "12b91ee8-a531-48d2-9a74-d77831204564" },
  { label: "Coordinator", value: "f2a12597-9259-40c9-8a0b-cea2008f0a94" },
];
export default function UserListPage() {
  const { data, mutate, error } = useSWR(`${apiBaseURL}/api/users`),
    { data: supervisorData } = useSWR(
      checkRoles(5) ? `${apiBaseURL}/api/users/fulltime` : null
    ),
    { data: locationData } = useSWR(`${apiBaseURL}/api/WorkCenter`),
    locationArray = useMemo(() => {
      return locationData?.data?.map((x) => ({ label: x.name, value: x.id }));
    }, [locationData]),
    { data: roleData } = useSWR(
      checkRoles(5) ? `${apiBaseURL}/api/Roles` : null
    ),
    navigate = useNavigate(),
    fetch = useFetch(navigate),
    { isXSmall, isSmall } = useScreenSize(),
    inputRef = useRef(null),
    [notifyResponse, setResponse] = useState({}),
    [showSpinner, setSpinner] = useState(false),
    //[showDeactivate, setDeactivate] = useState(false),
    [state, setState] = useState({
      users: [],
      filteredUsers: [],
      showFiltered: false,
      supervisors: [],
      roles: [],
      showDeactivate: false,
      addingUser: false,
      photoPopup: false,
      userId: "",
      photoURL: "",
      viewPassword: "password",
      showSpinnerModal: false,
    });

  useEffect(() => {
    if (checkRoles(5)) {
      if (data && supervisorData && roleData) {
        const supervisors = supervisorData.map((x) => ({
          label: `${x.firstName} ${x.lastName}`,
          value: x.id,
        }));
        const roles = roleData.map((x) => ({
          label: x.name.replace(/-/g, " "),
          value: x.id,
        }));

        const users = data.map((x) => ({
          id: x.id,
          email: x.email,
          firstName: x.firstName,
          lastName: x.lastName,
          internalUsername: x.internalUsername
            ? x.internalUsername.replace(/HQ\\/g, "")
            : null,
          roleIds:
            x.roles && x.roles.length > 0
              ? x.roles
                  .map((x) => x.id)
                  .reduce((result, item) => `${result},${item}`)
              : [],
          isStudent: x.isStudent,
          superVisorId: x.superVisorId,
          workCenter:
            x.assignedLocation !== null
              ? x.assignedLocation.id
              : x.assignedLocation,
          isNotificationsOn: x.isNotificationsOn,
          showImage: x.showImage,
          showName: x.showName,
          isFulltime: x.isFulltime,
          deactivated: x.deactivated,
        }));

        const filteredUsers = users.filter((x) => x.isStudent === true);
        setState({ ...state, users, supervisors, roles, filteredUsers });
      }
    } else {
      if (data) {
        const users = data.map((x) => ({
          id: x.id,
          email: x.email,
          firstName: x.firstName,
          lastName: x.lastName,
          internalUsername: x.internalUsername
            ? x.internalUsername.replace(/HQ\\/g, "")
            : null,
          roleIds:
            x.roles.length > 0
              ? x.roles
                  .map((x) => x.id)
                  .reduce((result, item) => `${result},${item}`)
              : [],
          isStudent: x.isStudent,
          superVisorId: x.superVisorId,
          workCenter:
            x.assignedLocation !== null
              ? x.assignedLocation.id
              : x.assignedLocation,
          isFulltime: x.isFulltime,
          deactivated: x.deactivated,
        }));

        const filteredUsers = users.filter((x) => x.isStudent === true);
        setState({ ...state, users, filteredUsers });
      }
    }
  }, [data, supervisorData, roleData]);

  const fetchSuccessful = (response) => {
    setResponse(response);
    return (
      response?.success &&
      response?.data?.statusCode &&
      response?.success !== "Invalid Model"
    );
  };

  const onShowPopup = () => {
    if (state.photoPopup) {
      const onUploadError = (e) => {
        return (e.message = "");
      };

      const onUploadStarted = async (e) => {
        const formdata = new FormData();
        formdata.append("UserId", state.userId);
        formdata.append("Image", e.file, e.file.name);

        let response = await fetch.form(
          `${apiBaseURL}/api/user/Profile`,
          formdata
        );
        console.log(response);
        if (fetchSuccessful(response)) {
          mutate();
        }
      };

      return (
        <MyPopup
          visible={state.photoPopup}
          width="50%"
          height="auto"
          fullScreen={isXSmall || isSmall}
          hideOnOutsideClick={true}
          onHiding={() => setState({ ...state, photoPopup: false })}
          title="Add Profile Picture"
        >
          <FileUploader
            uploadMode="instantly"
            onUploadStarted={onUploadStarted}
            onUploadError={onUploadError}
            maxFileSize={4000000}
          />
          <ToolbarItem
            widget="dxButton"
            toolbar="bottom"
            location="after"
            options={{
              text: "Close",
              useSubmitBehavior: true,
              onClick: (e) => {
                setState({ ...state, photoPopup: false });
              },
            }}
          />
        </MyPopup>
      );
    }
  };

  const renderGrid = () => {
    const isButtonVisible = (e) => {
      if (e.row.data.deactivated === false) {
        return !e.row.data.deactivated;
      }
    };

    const photoRender = (e) => {
      var imgBtn = document.querySelector("img");
      var fileInp = document.querySelector('[type="file"]');

      imgBtn.addEventListener("click", function () {
        fileInp.click();
      });

      const uploadPhoto = () => {
        inputRef.current.click();
      };

      const handleFileChange = async (event) => {
        const fileObj = event.target.files && event.target.files[0];
        if (!fileObj) {
          return;
        }

        const formdata = new FormData();
        formdata.append("UserId", state.userId);
        formdata.append("Image", event.target.files[0], fileObj.name);

        let response = await fetch.form(
          `${apiBaseURL}/api/user/Profile`,
          formdata
        );
        console.log(response);
        if (fetchSuccessful(response)) {
          mutate();
        }

        event.target.value = null;
      };

      return (
        <div>
          <input
            type="file"
            ref={inputRef}
            accept="image/jpg"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <img
            src={state.photoURL}
            width="30%"
            alt="Profile Pic"
            style={{ cursor: "pointer" }}
            onClick={() => uploadPhoto()}
          />
        </div>
      );
    };

    const onInitNewRow = async (e) => {
      e.data.overideUsername = false;
      setState({ ...state, addingUser: true });
    };
    const onEditingStart = async (e) => {
      let photo = await fetch.get(
        `${apiBaseURL}/api/user/Profile?userid=${e.data.id}`
      );
      setState({
        ...state,
        userId: e.data.id,
        photoURL: photo.data.data,
        addingUser: false,
      });
    };

    const onContentReady = (e) => {
      e.component.filter(
        "deactivated",
        "=",
        !state.showDeactivate ? false : true
      );
    };

    const onRowPrepared = (e) => {
      if (e.data) {
        if (e.data.deactivated) {
          e.rowElement.style.color = "#f63e4b";
        }
      }
      if (e.rowType === "header") {
        e.rowElement.style.backgroundColor = "#FFCD00";
        e.rowElement.style.fontWeight = "bold";
      }
    };

    const onRowRemoved = async (e) => {
      setSpinner(true);
      setState({ ...state, showSpinnerModal: true });

      const response = await fetch.delete(
        `${apiBaseURL}/api/users?UserId=${e.data.id}`
      );
      if (fetchSuccessful(response)) {
        mutate();
      }
      setSpinner(false);
    };

    const onReactivate = async (userId) => {
      setSpinner(true);

      const response = await fetch.post(
        `${apiBaseURL}/api/users?UserId=${userId}`
      );
      if (fetchSuccessful(response)) {
        mutate();
      }

      setSpinner(false);
    };

    const onRowUpdated = async (e) => {
      setSpinner(true);

      const roles = e.data.roleIds.split(",");
      const isFulltime =
        rolesOptions.find((x) => x.id === e.data.roleIds) === undefined
          ? false
          : true;
      const body = {
        email: e.data.email,
        firstName: e.data.firstName,
        lastName: e.data.lastName,
        roleIds: roles,
        supervisorId: e.data.superVisorId,
        workCenter: e.data.workCenter,
        isNotificationsOn: e.data.isNotificationsOn,
        showImage: e.data.showImage,
        showName: e.data.showName,
        isFulltime,
      };
      const response = await fetch.put(`${apiBaseURL}/api/users`, body);
      if (fetchSuccessful(response)) {
        mutate();
      }
      setSpinner(false);
    };

    const onRowInserted = async (e) => {
      setSpinner(true);
      const roles = e.data.roleIds.split(",");
      const isFulltime =
        rolesOptions.find((x) => x.id === e.data.roleIds) === undefined
          ? false
          : true;
      const body = {
        email: e.data.email,
        password: e.data.password,
        firstName: e.data.firstName,
        lastName: e.data.lastName,
        roleIds: roles,
        supervisorId: e.data.superVisorId,
        workCenter: e.data.workCenter,
        isNotificationsOn: e.data.isNotificationsOn,
        showImage: e.data.showImage,
        showName: e.data.showName,
        isFulltime,
      };
      const response = await fetch.post(
        `${apiBaseURL}/api/users/AddUser`,
        body
      );
      if (fetchSuccessful(response)) {
        mutate();
      }
      setSpinner(false);
    };

    if (data.length > 0) {
      return (
        <>
          <DataGrid
            dataSource={state.showFiltered ? state.filteredUsers : state.users}
            rowAlternationEnabled={false}
            allowColumnResizing={true}
            columnAutoWidth={true}
            repaintChangesOnly={true}
            showBorders={true}
            height="76vh"
            onContentReady={onContentReady}
            onRowPrepared={onRowPrepared}
            onRowRemoved={onRowRemoved}
            onRowInserted={onRowInserted}
            onRowUpdated={onRowUpdated}
            onInitNewRow={onInitNewRow}
            onEditingStart={onEditingStart}
          >
            <Scrolling mode="infinite" />
            <Editing
              allowAdding={checkRoles(5)}
              allowUpdating={checkRoles(5)}
              allowDeleting={checkRoles(5)}
              mode="popup"
            >
              <Popup
                title={state.addingUser ? "Add User" : "Edit User"}
                showTitle={true}
                fullScreen={isXSmall || isSmall}
                width={700}
                height={800}
              />
              <Form>
                <Item itemType="group" colCount={4} colSpan={2}>
                  <Item
                    dataField="photo"
                    colSpan={4}
                    visible={state.addingUser ? false : true}
                    render={photoRender}
                  />
                  <Item dataField="firstName" colSpan={2} isRequired={true} />
                  <Item dataField="lastName" colSpan={2} isRequired={true} />
                  <Item dataField="email" colSpan={4} isRequired={true} />
                  <Item
                    dataField="password"
                    editorType="dxTextBox"
                    editorOptions={{ mode: state.viewPassword }}
                    colSpan={4}
                    visible={state.addingUser}
                  />
                  <Item dataField="roleIds" colSpan={2} isRequired={true} />
                  <Item
                    dataField="superVisorId"
                    colSpan={2}
                    isRequired={true}
                  />
                  <Item dataField="workCenter" colSpan={4} isRequired={true} />
                  <Item
                    dataField="showImage"
                    editorType="dxCheckBox"
                    editorOptions={{
                      text: "Show Image",
                      elementAttr: {
                        class: "checkbox",
                      },
                    }}
                    colSpan={2}
                  >
                    <Label text={" "} />
                  </Item>
                  <Item
                    dataField="showName"
                    editorType="dxCheckBox"
                    editorOptions={{
                      text: "Show Name",
                      elementAttr: {
                        class: "checkbox",
                      },
                    }}
                    colSpan={2}
                  >
                    <Label text={" "} />
                  </Item>
                  <Item
                    dataField="isNotificationsOn"
                    editorType="dxCheckBox"
                    editorOptions={{
                      text: "Turn Notifications On",
                      elementAttr: {
                        class: "checkbox",
                      },
                    }}
                    visbile={checkRoles(5)}
                    colSpan={4}
                  >
                    <Label text={" "} />
                  </Item>
                </Item>
              </Form>
              <Texts
                confirmDeleteMessage={
                  !state.showDeactivate
                    ? "Are you sure you want to deactivate this user?"
                    : "Are you sure you want to reactivate this user?"
                }
              />
            </Editing>
            <SearchPanel
              id="searchpanel"
              visible={true}
              maxWidth={275}
              searchVisibleColumnsOnly={true}
              placeholder="Search..."
            />
            <Toolbar>
              <Item location="before">
                <h1 className="white">Users</h1>
              </Item>
              <Item locateInMenu="auto">
                <MyButton
                  text={
                    state.showFiltered ? "Show All Users" : "Show My Employees"
                  }
                  type="normal"
                  stylingMode="contained"
                  onClick={() =>
                    state.showFiltered
                      ? setState({ ...state, showFiltered: false })
                      : setState({ ...state, showFiltered: true })
                  }
                />
              </Item>
              <Item name="addRowButton" />
              <Item name="searchPanel" />
            </Toolbar>

            <Column type="buttons" name="Actions">
              <Button
                text="View Hours"
                icon="event"
                visible={isButtonVisible}
                onClick={(params) =>
                  navigate(`/users/${params.row.data.id}`, {
                    state: {
                      userName: `${params.row.data.firstName} ${params.row.data.lastName}`,
                      userWC: params.row.data.workCenter,
                    },
                  })
                }
              />
              <Button
                name="edit"
                visible={checkRoles(5) ? isButtonVisible : false}
              />
              <Button
                name="delete"
                text="Deactivate"
                visible={checkRoles(5) ? isButtonVisible : false}
              />
              <Button
                name="delete"
                text="Reactivate"
                icon="user"
                visible={checkRoles(5) ? state.showDeactivate : false}
                onClick={(params) => onReactivate(params.row.data.id)}
              />
            </Column>
            <HeaderFilter visible={true} />
            <Column dataField="id" visible={false} />
            <Column dataField="photo" visible={false} />
            <Column
              dataField="firstName"
              minWidth={200}
              allowHeaderFiltering={false}
            />
            <Column
              dataField="lastName"
              minWidth={200}
              sortIndex={1}
              sortOrder="asc"
              allowHeaderFiltering={false}
            />
            <Column
              dataField="email"
              minWidth={300}
              allowHeaderFiltering={false}
            >
              <EmailRule message="Email is invalid" />
            </Column>
            <Column dataField="internalUsername" visible={false}>
              {state.addingUsesr ? (
                <PatternRule
                  message="Invalid Internal Username. Please check the Override Username button if this is not a mistake"
                  pattern={`${state.users.firstName}.${state.users.lastName}`}
                />
              ) : null}
            </Column>
            <Column dataField="overideUsername" visible={false} />
            <Column dataField="password" visible={false} />
            <Column dataField="workCenter" visible={true} minWidth={200}>
              <Lookup
                dataSource={locationArray}
                valueExpr="value"
                displayExpr="label"
              />
            </Column>
            <Column
              dataField="roleIds"
              visible={checkRoles(5)}
              caption="Roles"
              minWidth={200}
            >
              <Lookup
                dataSource={state.roles}
                valueExpr="value"
                displayExpr="label"
              />
            </Column>
            <Column
              dataField="superVisorId"
              visible={true}
              caption="Supervisor"
            >
              <Lookup
                dataSource={state.supervisors}
                valueExpr="value"
                displayExpr="label"
              />
            </Column>
            <Column
              dataField="isFullTime"
              caption="Full Time"
              dataType="boolean"
              minWidth={100}
              visible={false}
            />
            <Column dataField="isNotificationsOn" visible={false} />
            <Column dataField="showImage" visible={false} />
            <Column dataField="showName" visible={false} />
          </DataGrid>
        </>
      );
    } else {
      return checkRoles(5) ? (
        <h5 className="mb-0 text-center">
          A user has not been created yet. Create a user
          <Link
            to="/users/new"
            className="ms-2 hoverLinkText yellow d-inline-block pointer"
          >
            here.
          </Link>
        </h5>
      ) : (
        <h5 className="mb-0 text-center">
          No users assigned to you. Contact admin to assigned users
        </h5>
      );
    }
  };

  if (!data && !error) return <LoadingSpinnerModal show />;

  return (
    <div className="px-lg-4 content">
      <ActionNotifications response={notifyResponse} />
      {showSpinner && <LoadingSpinnerModal show={true} />}
      <div className="px-2 page-content">
        <div className="boxCotainerWrapper whiteBG my-3 contentBox">
          {!error ? (
            <>
              {onShowPopup()}
              {renderGrid()}
              {checkRoles(5) && (
                <>
                  <br />
                  <MyButton
                    visible={checkRoles(5)}
                    text={
                      state.showDeactivate
                        ? "Active Users"
                        : "Deactivated Users"
                    }
                    type="normal"
                    stylingMode="contained"
                    onClick={() =>
                      state.showDeactivate
                        ? setState({ ...state, showDeactivate: false })
                        : setState({ ...state, showDeactivate: true })
                    }
                  />
                </>
              )}
            </>
          ) : (
            <ErrorMessage />
          )}
        </div>
      </div>
    </div>
  );
}
